import { MODEL_VERSION } from '@/app/constants';
import type { FormTabType, ModelVersion, TaskSettings } from '@/app/types';

export interface ModelVersionTrialState {
  remainingTrialsQ2Pro?: number;
  remainingTrialsQ2T2V?: number;
  remainingTrialsQ2C2V?: number;
  remainingTrialsQ2T2I?: number;
  remainingTrialsQ2R2I?: number;
  remainingTrialsImage25T2IFast?: number;
  remainingTrialsImage25T2IPro?: number;
  remainingTrialsImage25R2IFast?: number;
  remainingTrialsImage25R2IPro?: number;
  remainingTrialsQ3T2VI2V?: number;
  remainingTrialsQ3C2V?: number;
  trialsQ3T2VI2VDurations?: number[];
  trialsQ3C2VDurations?: number[];
}

const getPositiveRemainingTrials = (remainingTrials?: number) =>
  typeof remainingTrials === 'number' && remainingTrials > 0 ? remainingTrials : undefined;

const isTrialDurationIncluded = (settings?: TaskSettings, durations?: number[]) =>
  !!settings?.duration && (durations?.includes(Number(settings.duration)) ?? false);

const getImage25RemainingTrials = ({
  type,
  modelVersion,
  trials,
}: {
  type: Exclude<FormTabType, 'scene'>;
  modelVersion: ModelVersion;
  trials: ModelVersionTrialState;
}) => {
  const isFast = modelVersion === MODEL_VERSION.GPT_IMAGE_2_5_FAST;
  const isPro = modelVersion === MODEL_VERSION.GPT_IMAGE_2_5_PRO;
  if (!isFast && !isPro) {
    return undefined;
  }

  if (type === 'text2image') {
    return getPositiveRemainingTrials(
      isFast ? trials.remainingTrialsImage25T2IFast : trials.remainingTrialsImage25T2IPro,
    );
  }
  if (type === 'reference2image') {
    return getPositiveRemainingTrials(
      isFast ? trials.remainingTrialsImage25R2IFast : trials.remainingTrialsImage25R2IPro,
    );
  }
  return undefined;
};

export const getModelVersionRemainingTrials = ({
  type,
  modelVersion,
  settings,
  trials,
}: {
  type: Exclude<FormTabType, 'scene'>;
  modelVersion: ModelVersion;
  settings?: TaskSettings;
  trials: ModelVersionTrialState;
}) => {
  if (modelVersion === MODEL_VERSION.GPT_IMAGE_2_5_FAST || modelVersion === MODEL_VERSION.GPT_IMAGE_2_5_PRO) {
    return getImage25RemainingTrials({ type, modelVersion, trials });
  }

  if (modelVersion === MODEL_VERSION.Q3) {
    if (
      (type === 'text2video' || type === 'img2video') &&
      isTrialDurationIncluded(settings, trials.trialsQ3T2VI2VDurations)
    ) {
      return getPositiveRemainingTrials(trials.remainingTrialsQ3T2VI2V);
    }

    if (type === 'character2video' && isTrialDurationIncluded(settings, trials.trialsQ3C2VDurations)) {
      return getPositiveRemainingTrials(trials.remainingTrialsQ3C2V);
    }
  }

  if (modelVersion === MODEL_VERSION.Q2_PRO && type === 'character2video') {
    return getPositiveRemainingTrials(trials.remainingTrialsQ2Pro);
  }

  if (modelVersion !== MODEL_VERSION.Q2) {
    return undefined;
  }

  switch (type) {
    case 'text2video':
      return getPositiveRemainingTrials(trials.remainingTrialsQ2T2V);
    case 'character2video':
      return getPositiveRemainingTrials(trials.remainingTrialsQ2C2V);
    case 'text2image':
      return getPositiveRemainingTrials(trials.remainingTrialsQ2T2I);
    case 'reference2image':
      return getPositiveRemainingTrials(trials.remainingTrialsQ2R2I);
    default:
      return undefined;
  }
};
